import { bundleHasDiscontinuedProduct, bundleHasHiddenProduct,
  bundleHasOOSProduct, bundleHasObsoleteProduct } from './product-unavailable-utils';
import { getItemPrices, bundleHasItemLevelMapHeadroom } from './product-bundle-utils';

export const parsePriceValue = (price) => {
  if (!price) {
    return null;
  }
  const priceParts = {
    dollars: 0,
    cents: 0
  };
  const priceSplit = price.toString().split('.');
  priceParts.dollars = priceSplit[0];
  priceParts.cents = priceSplit[1] || '00';
  if (priceParts.cents.length === 1) {
    priceParts.cents += '0';
  }
  return priceParts;
};

// NOTE: This is a second, standalone bundle-pricing path used only by BundleMini. It intentionally
// duplicates the promo-summation, parent-adjustment fallback, and MAP-headroom suppression that the
// main summary path centralizes in extractBundleAdjustments + getBundlePriceCalculations. The two are
// NOT behavior-identical (this path prefers child adjustments and has no isBundleModified context), so
// they were deliberately left separate. Revisit and consolidate when refactoring bundle-mini (future Jira).
export const calculateTotal = (products = [], parentBundleAdjustments = []) => {
  let firstPriceTotal = 0;
  let secondPriceTotal = 0;
  let showMapMessage = false;
  let additionalSavings = 0;
  let hasEffectiveMap = false;

  products.forEach((item) => {
    const { pricing } = item;
    if (!pricing) { return; }

    const { firstPrice, secondPrice } = getItemPrices(item);
    firstPriceTotal += firstPrice;
    secondPriceTotal += secondPrice;

    if (pricing.mapAboveOriginalPrice !== null) {
      showMapMessage = true;
    }
    if (pricing?.mapDetail?.effectiveMap) {
      hasEffectiveMap = true;
    }

    const adjustments = pricing.bundlePromotionalAdjustments || [];
    adjustments.forEach((adj) => {
      if (adj.promotionTag) {
        // Bundle-level promotions are already the total for the whole bundle — apply as-is,
        // never multiplied by the item quantity (only item-level prices scale by quantity).
        additionalSavings += (adj.dollarOff || 0);
      }
    });
  });

  // If no child products had adjustments, use parent bundle adjustments
  if (additionalSavings === 0 && parentBundleAdjustments?.length) {
    parentBundleAdjustments.forEach((adj) => {
      if (adj.promotionTag) {
        additionalSavings += (adj.dollarOff || 0);
      }
    });
  }

  // Mirror useBundlePricingDetails: MAP bundle with no item-level headroom => don't subtract bundle promos.
  if (hasEffectiveMap && !bundleHasItemLevelMapHeadroom(products)) {
    additionalSavings = 0;
  }

  const totalValue = Math.max(secondPriceTotal - additionalSavings, 0);
  const totalSavings = firstPriceTotal - totalValue;

  return {
    original: firstPriceTotal,
    totalValue: parsePriceValue(totalValue || firstPriceTotal),
    totalSavings,
    hasTotalSavings: totalValue > 0 && totalSavings > 0,
    percentage: (totalSavings && totalValue ? ((totalSavings / firstPriceTotal) * 100) : 0),
    showMapMessage
  };
};

export const getBundleWithPricing = ({ bundle = {}, pricing = {} }) => {
  // Exclude __typename to prevent Price component from treating the bundle
  // as a BaseProduct (which triggers restricted-storefront checks for exchange users)
  const { __typename: _typename, ...bundleData } = bundle || {}; // eslint-disable-line no-unused-vars
  return {
    ...bundleData,
    pricing,
    info: {
      ...bundleData?.info,
      sskMax: null,
      sskMin: null
    },
    disableRangePricing: true,
    omitDetails: false,
    showStartingAt: false
  };
};

export const getBundleStickyNav = ({ bundle = {}, pricing = {}, products = [] }) => {
  const bundleWithPricing = getBundleWithPricing({ bundle, pricing });
  const { unavailableStickyNav } = bundle?.features || {};
  return (
    {
      ...bundleWithPricing,
      bundleSpecificationDetails: {
        ...bundleWithPricing.bundleSpecificationDetails,
        isOOS: unavailableStickyNav && bundleHasOOSProduct({ products }),
        isDiscontinued: unavailableStickyNav && bundleHasDiscontinuedProduct({ products }),
        isHiddenItem: unavailableStickyNav && bundleHasHiddenProduct({ products }),
        isObsoleteItem: unavailableStickyNav && bundleHasObsoleteProduct({ products })
      }
    }
  );
};